import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
  /** Plural noun for the arrow labels, e.g. "programs". */
  label: string;
  /** Scrolls the tab carrying this `data-tab-key` into view when it changes. */
  activeKey?: string;
  className?: string;
  /** Tab buttons, each tagged with `data-tab-key`. */
  children: ReactNode;
}

/**
 * Horizontal tab strip with chevron arrows on BOTH sides.
 *
 * The arrows are always rendered, dimmed when that direction has nothing left to scroll,
 * rather than appearing only once the strip overflows. A control that materializes on
 * scroll can't teach you the strip scrolls in the first place — showing both ends up front
 * makes the affordance legible on a phone where the overflow is off-screen.
 */
export function ScrollTabs({ label, activeKey, className = '', children }: Props) {
  const navRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, children]);

  // Keep the selected tab visible when landing straight on a view whose tab sits
  // off-screen in the scroller.
  useEffect(() => {
    if (!activeKey) return;
    // Tab keys are free text (program names, school names), so they must be escaped before
    // going into a selector. CSS.escape is absent in some test environments.
    const escaped = typeof CSS !== 'undefined' && CSS.escape
      ? CSS.escape(activeKey)
      : activeKey.replace(/["\\]/g, '\\$&');
    const tab = navRef.current?.querySelector(`[data-tab-key="${escaped}"]`);
    // Guarded: jsdom has no layout engine and does not implement scrollIntoView.
    tab?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [activeKey]);

  const nudge = (dir: 'left' | 'right') => {
    navRef.current?.scrollBy({ left: dir === 'left' ? -160 : 160, behavior: 'smooth' });
  };

  // Solid, not a gradient: the arrow columns sit ON TOP of the scroller and fully mask it,
  // so a tab scrolling past is cut off cleanly at the arrow instead of bleeding through
  // half-faded underneath it.
  const arrowClass = (enabled: boolean) =>
    `absolute inset-y-0 z-20 flex items-center justify-center w-9 bg-white transition-colors ${
      enabled ? 'text-gray-500 hover:text-gray-900' : 'text-gray-300 cursor-default'
    }`;

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => nudge('left')}
        disabled={!canScrollLeft}
        aria-label={`Scroll ${label} left`}
        className={`${arrowClass(canScrollLeft)} left-0`}
      >
        <ChevronLeft className="h-5 w-5" />
      </button>

      {/* px-9 parks the first and last tab clear of the arrow columns at rest. */}
      <nav ref={navRef} className="flex gap-1 overflow-x-auto no-scrollbar px-9">
        {children}
      </nav>

      <button
        type="button"
        onClick={() => nudge('right')}
        disabled={!canScrollRight}
        aria-label={`Scroll ${label} right`}
        className={`${arrowClass(canScrollRight)} right-0`}
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
}
